import CloseIcon from "@mui/icons-material/Close";
import {
    AppBar,
    Box,
    Button,
    Dialog,
    IconButton,
    Paper,
    Stack,
    Toolbar,
    Typography,
} from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { type ReactNode, useCallback, useState } from "react";
import { useConfirm } from "react-confirm-hook";
import { match } from "ts-pattern";
import { z } from "zod/mini";
import logo from "@/assets/logo.svg";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useCreateAuctionSessionEventMutation } from "@/mutations/auction-session-event.ts";
import { useQueryOptionsFactory } from "@/queries";
import MultiParcelAuctionView from "@/routes/manage/auctions/$auctionId/run/-components/MultiParcelAuctionView/MultiParcelAuctionView.js";
import RunContextProvider from "@/routes/manage/auctions/$auctionId/run/-components/RunContextProvider.tsx";
import SingleParcelAuctionView from "@/routes/manage/auctions/$auctionId/run/-components/SingleParcelAuctionView/SingleParcelAuctionView.js";
import { defaultMutationErrorHandler } from "@/utils/api.ts";
import ChoiceAuctionView from "./-components/ChoiceAuctionView/ChoiceAuctionView.tsx";

const Root = (): ReactNode => {
    const preview = Route.useSearch().preview;
    const qof = useQueryOptionsFactory();
    const { auctionId } = Route.useParams();
    const auction = useSuspenseQuery(qof.auction.get(auctionId)).data;

    const sessionQuery = preview
        ? qof.auctionSession.getPreview(auctionId)
        : qof.auctionSession.get(auctionId);

    const auctionSession = useSuspenseQuery(sessionQuery).data;
    const registrations = useSuspenseQuery(
        qof.registration.list(auctionId, { includeOpeningBids: true }),
    ).data;
    const [open, setOpen] = useState(true);
    const navigate = useNavigate();
    const createEventMutation = useCreateAuctionSessionEventMutation();
    const confirm = useConfirm(ConfirmDialog);

    const handleCloseAuction = useCallback(() => {
        confirm({
            title: "End auction",
            message: `Are you sure you want to end the auction for "${auction.title}"? This action cannot be undone and will finalize all bids.`,
            confirmLabel: "End auction",
            denyLabel: "Cancel",
            onConfirm: async () => {
                try {
                    await createEventMutation.mutateAsync({
                        auctionId,
                        data: {
                            type: "close",
                        },
                    });
                    enqueueSnackbar("Auction has been ended", { variant: "success" });
                    setOpen(false);
                } catch (error) {
                    defaultMutationErrorHandler(error);
                }
            },
        });
    }, [createEventMutation, confirm, auctionId, auction.title]);

    return (
        <Dialog
            open={open}
            onClose={() => {
                setOpen(false);
            }}
            fullScreen
            slotProps={{
                transition: {
                    onExited: () => {
                        void navigate({
                            to: "/manage/auctions",
                            search: ({ preview, ...rest }) => rest,
                        });
                    },
                },
                paper: {
                    sx: {
                        backgroundColor: "background.default",
                    },
                },
            }}
        >
            <Stack sx={{ height: "100%" }}>
                <AppBar position="static">
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => {
                                setOpen(false);
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography variant="h6">{auction.title}</Typography>
                    </Toolbar>
                </AppBar>
                <Box sx={{ p: 4, flexGrow: 1, overflow: "scroll" }}>
                    <RunContextProvider
                        auction={auction}
                        auctionSession={auctionSession}
                        registrations={registrations}
                    >
                        {match(auction.config)
                            .with({ type: "single_parcel" }, (config) => (
                                <SingleParcelAuctionView auctionConfig={config} />
                            ))
                            .with({ type: "choice" }, (config) => (
                                <ChoiceAuctionView config={config} />
                            ))
                            .otherwise((config) => (
                                <MultiParcelAuctionView auctionConfig={config} />
                            ))}
                    </RunContextProvider>
                </Box>
                {auction.state === "running" && (
                    <Paper sx={{ display: "flex", py: 1, px: 3, alignItems: "center" }}>
                        <Box
                            component="img"
                            src={logo}
                            sx={{
                                display: "block",
                                height: 48,
                            }}
                        />

                        <Button
                            variant="contained"
                            onClick={handleCloseAuction}
                            sx={{ ml: "auto" }}
                        >
                            End auction
                        </Button>
                    </Paper>
                )}
            </Stack>
        </Dialog>
    );
};

const runSearchSchema = z.object({
    preview: z._default(z.catch(z.coerce.boolean(), false), false),
});

export const Route = createFileRoute("/manage/auctions/$auctionId/run")({
    component: Root,
    validateSearch: runSearchSchema,
    loaderDeps: ({ search: { preview } }) => ({ preview }),
    loader: async ({ context, params, deps }) => {
        const sessionQuery = deps.preview
            ? context.qof.auctionSession.getPreview(params.auctionId)
            : context.qof.auctionSession.get(params.auctionId);

        await Promise.all([
            context.queryClient.ensureQueryData(sessionQuery),
            context.queryClient.ensureQueryData(
                context.qof.registration.list(params.auctionId, { includeOpeningBids: true }),
            ),
        ]);
    },
});
